import type {
  ButtonHTMLAttributes,
  Dispatch,
  PropsWithChildren,
  ReactEventHandler,
  ReactNode,
  Ref,
} from "react";
import { Fragment } from "react";
import { pipe } from "fp-ts/lib/function";
import { none } from "fp-ts/lib/Option";
import { Key } from "ts-key-enum";

import type { SVGString } from "*.svg";

import { E, O } from "@scripts/fp-ts";
import { Svg } from "@scripts/react/components/Svg";
import type { ApplyKlassNullable, KlassProp } from "@scripts/react/util/classnames";
import { klass, klassConditional, klassProp } from "@scripts/react/util/classnames";
import type { AnyPageMeta } from "@scripts/routes/routing/base";
import type { AriaLabelRequired, ChildStringOrAriaLabel } from "@scripts/types/ariaLabel";
import { delProp } from "@scripts/util/delProp";
import type { LabelOrAriaLabel } from "@scripts/util/labelOrAriaLabel";
import { merge } from "@scripts/util/merge";

import checkmark from "@svgs/checkmark.svg";
import chevronDown from "@svgs/chevron-down.svg";
import closeX from "@svgs/close-x.svg";
import download from "@svgs/download.svg";
import pencil from "@svgs/pencil.svg";
import plus from "@svgs/plus.svg";
import reject from "@svgs/reject.svg";

import { mapOrEmpty } from "../components/Empty";
import { getNavigateBackFn } from "../router";
import type { RouterAction } from "../state/router";
import type { PageHistoryOldToNew } from "../state/store";
import type { WithHTMLAttrs } from "../util/dom";
import { AnchorIconLayout, type AnchorIconLayoutProps, type ArrowType, arrowTypeKlassO } from "./Anchor";
import { Image } from "./Image";
import { LoaderBars } from "./Loader";
import { LoadingCircle } from "./LoadingCircle";

export type Variant = "primary" | "secondary" | "link" | "none" | "callout" | "action" | "danger" | "reject" | "danger-secondary" | "gray";

type ButtonPropsI = {
  children: ReactNode;
  onClick: ReactEventHandler;
  variant: Variant;
  ref?: Ref<HTMLButtonElement>;
};

export type ButtonProps = ApplyKlassNullable<WithHTMLAttrs<ButtonPropsI, ButtonHTMLAttributes<HTMLElement>>>;

interface ButtonVariantI {
  children: ReactNode;
  onClick: ReactEventHandler;
  ref?: Ref<HTMLButtonElement>;
}

export type ButtonVariantProps = ApplyKlassNullable<WithHTMLAttrs<ButtonVariantI, ButtonHTMLAttributes<HTMLElement>>>;

export const Button = ({ variant, onClick, children, ...props }: ButtonProps) => {
  const defaultAttributes: ButtonHTMLAttributes<HTMLElement> = {
    ...klass(
      variant !== "none" ? `btn-${variant}` : none,
      variant !== "link" ? "btn" : none,
      props.klass
    ),
    onClick: onClick,
  };
  const mergedAttrs = merge(delProp(props, "klass"))(defaultAttributes);

  return (
    <button type="button" {...mergedAttrs}>
      {children}
    </button>
  );
};

export const ButtonPrimary = ({ children, ...props }: ButtonVariantProps) => (
  <Button variant="primary" {...props}>{children}</Button>
);

export const ButtonGray = ({ children, ...props }: ButtonVariantProps) => (
  <Button variant="gray" {...props}>{children}</Button>
);

export const ButtonSecondary = ({ children, ...props }: ButtonVariantProps) => (
  <Button variant="secondary" {...props}>{children}</Button>
);

export const ButtonDanger = ({ children, ...props }: ButtonVariantProps) => (
  <Button variant="danger" {...props}>{children}</Button>
);

export const btnLinkSmallStyles = ["small", "mb-025", "b-0"] as const;

type ButtonLinkProps = ButtonVariantProps & ArrowType;

export const ButtonLink = ({ children, ...props }: ButtonLinkProps) => (
  <Button
    variant="link"
    {...delProp(props, "arrowType")}
    klass={klassProp(arrowTypeKlassO(O.fromNullable(props.arrowType)), props.klass)}
  >{children}</Button>
);

type ButtonLinkMultilineProps = Omit<ButtonVariantProps, "children"> & { text: string };
export const ButtonLinkMultiline = (props: ButtonLinkMultilineProps) => (
  <ButtonLink
    {...props}
    klass={klassProp("btn-link-multiline", "text-left", props.klass)}
  >
    <span>
      {props.text}
    </span>
  </ButtonLink>
);

export const InputAlignedButtonLink = (props: PropsWithChildren<ButtonVariantProps>) =>
  <ButtonLink {...props} klass={klassProp("no-decoration", "input-aligned", props.klass)}>
    {props.children}
  </ButtonLink>;

type ButtonLoading<LT extends string> = {
  loading: boolean;
  text: ReactNode;
  loadingText: LT extends `${string}${"..." | "…" | "&hellip;"}` ? never : LT;
};

export const LoadingElement = <LT extends string>(props: ButtonLoading<LT>) => props.loading
  ? <Fragment><LoaderBars />{props.loadingText}&hellip;</Fragment>
  // eslint-disable-next-line react/jsx-no-useless-fragment
  : <Fragment>{props.text}</Fragment>;

type ButtonSubmitProps<LT extends string> = ApplyKlassNullable<WithHTMLAttrs<ButtonLoading<LT> & {
    onClick: ReactEventHandler;
    variant?: Variant;
    ref?: Ref<HTMLButtonElement>;
}, ButtonHTMLAttributes<HTMLElement>>>;

export const ButtonSubmit = <LT extends string>(props: Omit<ButtonSubmitProps<LT>, "children">) => (
  <Button
    type="submit"
    {...delProp(props, "loading", "loadingText", "text")}
    variant={props.variant ?? "primary"}
    disabled={props.loading || props.disabled}
  >
    <LoadingElement {...delProp(props, "ref")} />
  </Button>
);


export const ButtonAsync = <LT extends string>(props: Omit<ButtonProps & ButtonLoading<LT>, "children">) => {
  return <Button {...delProp(props, "loading", "loadingText", "text")} disabled={props.loading || props.disabled}>
    <LoadingElement {...delProp(props, "ref")} />
  </Button>;
};

const ButtonCalloutIcon = (props: { icon: SVGString }) => <>
  <Svg src={props.icon} klass="callout-icon" />
  <span {...klass("callout-plus")}>
    <Svg src={plus} />
  </span>
</>;

export const ButtonCallout = ({ children, icon, ...props }: ButtonVariantProps & { icon: SVGString }) => (
  <Button variant="callout" {...props}>
    <ButtonCalloutIcon icon={icon} />
    <span>{children}</span>
  </Button>
);

export const ButtonCalloutAsync = ({ children, icon, ...props }: ButtonVariantProps & { icon: SVGString, loading: boolean }) => (
  <Button variant="callout" {...delProp(props, "loading")} disabled={props.loading || props.disabled}>
    {props.loading
      ? <div {...klass("callout-icon")}><LoadingCircle loadingMsg="" /></div>
      : <ButtonCalloutIcon icon={icon} />
    }
    <span>{children}</span>
  </Button>
);

export const ButtonSmall = ({ onClick, children, variant, ...props }: ButtonProps) => (
  <Button
    {...props}
    variant={variant}
    onClick={onClick}
    klass={klassProp("btn-small", props.klass)}
  >
    {children}
  </Button>
);

interface SizeButtonProps extends ButtonVariantProps {
  small: boolean;
}

export const ButtonApprove = ({ onClick, children, small, ...props }: SizeButtonProps) => (
  <Button
    onClick={onClick}
    variant={"none"}
    {...props}
    klass={klassProp("btn-approve", klassConditional("btn-small")(small), props.klass)}
  >
    <Svg src={checkmark} />
    {children}
  </Button>
);

export const ButtonReject = ({ onClick, children, small, ...props }: SizeButtonProps) => (
  <Button
    variant={"none"}
    onClick={onClick}
    {...props}
    klass={klassProp("btn-reject", small ? "btn-small" : none, props.klass)}
  >
    <Svg src={reject} />
    {children}
  </Button>
);

export const ButtonEdit = ({ onClick, children, ...props }: ButtonVariantProps) => (
  <Button
    variant={"none"}
    onClick={onClick}
    {...props}
    klass={klassProp("btn-review", "btn-small", props.klass)}
  >
    <Svg src={pencil} />
    {children}
  </Button>
);

export const ButtonEditLoading = ({ children, ...props }: ButtonVariantProps) => (
  <Button variant="none" {...props} disabled klass={klassProp("btn-review", "btn-small", props.klass)}>
    <LoaderBars />
    {children}
  </Button>
);


type ChevronProps = {
  chevronOpen: boolean;
};
export const ButtonReview = ({ onClick, children, small, chevronOpen, ...props }: SizeButtonProps & ChevronProps) => (
  <Button
    variant={"none"}
    onClick={onClick}
    {...props}
    klass={klassProp("btn-review", small ? "btn-small" : none, "rotate-ccw-180-svg", klassConditional("chevron-open", "rotate")(chevronOpen), props.klass)}
  >
    <Svg src={chevronDown} />
    {children}
  </Button>
);

type IconButtonBase = {
  onClick: ReactEventHandler;
  icon: SVGString;
  ref?: Ref<HTMLButtonElement>;
};

type ButtonIconProps = IconButtonBase & ChildStringOrAriaLabel & {
  linkPostfix?: ReactNode;
  variant: Exclude<Variant, "link">;
};

type IconButtonWithHtmlAttributes<T> = ApplyKlassNullable<WithHTMLAttrs<T, ButtonHTMLAttributes<HTMLElement>>>;

export type ButtonIconWithHtmlAttributes = IconButtonWithHtmlAttributes<ButtonIconProps>;

export const ButtonIcon = ({ onClick, variant, icon, linkPostfix, children, ...props }: ButtonIconWithHtmlAttributes) => (
  <Button onClick={onClick} variant={variant} {...props}>
    <Svg src={icon} />
    <span>{children}</span>
    {linkPostfix}
  </Button>
);

type ButtonLinkIconProps = Omit<ButtonIconWithHtmlAttributes, "variant" | "children" | "aria-label"> &
  Pick<AnchorIconLayoutProps, "appendIcon"> & { textOrAriaLabel: LabelOrAriaLabel };

export const ButtonLinkIcon = ({ onClick, icon, textOrAriaLabel, appendIcon, ...props }: ButtonLinkIconProps) => (
  <Button
    onClick={onClick}
    variant={"link"}
    {...props}
    klass={klassProp("no-decoration", "b-0", props.klass)}
    aria-label={E.toUnion(textOrAriaLabel)}
  >
    <AnchorIconLayout
      appendIcon={appendIcon}
      disabled={props.disabled || false}
      icon={icon}
      textOrAriaLabel={textOrAriaLabel}
    />
  </Button>
);

export const ButtonIconStacked = ({ onClick, variant, icon, linkPostfix, children, ...props }: ButtonIconWithHtmlAttributes) => (
  <Button onClick={onClick} variant={variant} {...props}>
    <div {...klass("btn-stacked")}>
      <Svg src={icon} klass="stacked-icon" />
      <span>{children}</span>
      {linkPostfix}
    </div>
  </Button>
);

export const ButtonAsyncIcon = <LT extends string>({ onClick, variant, icon, linkPostfix, children, ...props }: ButtonIconWithHtmlAttributes & Omit<ButtonLoading<LT>, "text">) => (
  <Button onClick={onClick} variant={variant} {...delProp(props, "loading", "loadingText")}>
    <LoadingElement loading={props.loading} loadingText={props.loadingText} text={
      <Fragment>
        <Svg src={icon} />
        <span>{children}</span>
        {linkPostfix}
      </Fragment>
    }
    />
  </Button>
);

type IconButtonProps = IconButtonWithHtmlAttributes<IconButtonBase> & AriaLabelRequired;

const IconButtonWithChildren = ({ onClick, ...props }: Omit<IconButtonProps, "icon">) => (
  <button
    type="button"
    onClick={onClick}
    {...props}
    {...klass("icon-button", props.klass, klassConditional("disabled")(props.disabled ?? false))}
  >
    {props.children}
  </button>
);

export const IconButton = (props: Omit<IconButtonProps, "children">) => (
  <IconButtonWithChildren {...delProp(props, "icon")}>
    <Svg src={props.icon} />
  </IconButtonWithChildren>
);

type ButtonAction = "edit" | "delete" | "download";

type IconType = {
  type: "svg";
  svg: SVGString;
};

type ImageType = {
  type: "img";
  src: string;
};

export type ButtonIconType = IconType | ImageType;

type ActionIcons = {
  [key in ButtonAction]: SVGString;
};

const actionIcons: ActionIcons = {
  edit: pencil,
  delete: closeX,
  download: download,
};

export type ButtonActionProps = PropsWithChildren<{
  action: ButtonAction;
  contentName: string;
  disabled?: boolean;
  onActionClick: ReactEventHandler;
  onClick: ReactEventHandler;
  variant: "tall" | "short";
  disableHover?: boolean;
}>;

const ButtonActionBaseIcon = (props: Omit<ButtonActionProps, "onClick" | "variant" | "contentName">) => <ButtonLink
  aria-label={props.action}
  klass="action-type"
  tabIndex={0}
  onClick={props.onActionClick}
  onKeyPress={(e) => (e.key === Key.Enter || e.key === "SpaceBar" || e.key === " ") && props.onActionClick(e)}
  disabled={props.disabled}
>
  <Svg src={actionIcons[props.action]} />
</ButtonLink>;

const ButtonActionBase = (props: ButtonActionProps) =>
  <div
    {...klass("btn", `btn-action-${props.variant}`, klassConditional("no-hover")(props.disableHover ?? false))}
    {...(props.disabled && { "data-disabled": true })}
  >
    <Button
      klass={"content"}
      variant={"link"}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      {props.children}
    </Button>
    <ButtonActionBaseIcon {...props} />
  </div>;

export const ButtonActionShort = (props: Omit<ButtonActionProps, "variant">) => {
  return (
    <ButtonActionBase
      action={props.action}
      onActionClick={props.onActionClick}
      onClick={props.onClick}
      contentName={props.contentName}
      variant={"short"}
      disableHover={props.disableHover}
    >
      <div {...klass("content-label")}>
        <span {...klass("content-name")}>{props.contentName}</span>
      </div>
    </ButtonActionBase>
  );
};

export type ButtonActionTallProps = Omit<ButtonActionProps, "variant" | "children"> & {
  contentMeta: O.Option<string>;
  icon: ButtonIconType;
};

const imageComponentProps = klassProp("content-type");

export const ButtonActionTall = (props: ButtonActionTallProps) =>
  <ButtonActionBase
    action={props.action}
    onActionClick={props.onActionClick}
    onClick={props.onClick}
    contentName={props.contentName}
    variant={"tall"}
    disableHover={props.disableHover}
  >
    {props.icon.type === "img"
      ? <div {...klass(imageComponentProps)}><Image src={props.icon.src} alt={"ImageLabel"} /></div>
      : <Svg klass={imageComponentProps} src={props.icon.svg} />
    }
    <div {...klass("content-label")}>
      <span {...klass("content-name", klassConditional("has-meta")(O.isSome(props.contentMeta)))}>
        {props.contentName}
      </span>
      {pipe(props.contentMeta, mapOrEmpty(t => <span {...klass("content-meta")}>{t}</span>))}
    </div>
  </ButtonActionBase>;

export type CloseXButtonProps = Omit<IconButtonProps, "icon" | "aria-label">;

export const CloseXButton = ({ ...props }: CloseXButtonProps) => (
  <IconButton icon={closeX} aria-label="close" {...props} />
);

export const PlusButton = (props: { icon: SVGString, text: string }) => <>
  <div {...klass("icon-container")}>
    <span {...klass("callout-plus")}>
      <Svg src={plus} />
    </span>
    <Svg src={props.icon} />
  </div>
  {props.text}
</>;

export const ButtonsContainer = (props: PropsWithChildren<{ klass?: KlassProp, divider?: true }>) => (
  <div {...klass("buttons-container", klassConditional("divider")(!!props.divider), props.klass)}>
    {props.children}
  </div>
);

const secondToLastIndex = 2;

type ButtonLinkPageLastProps = Omit<ButtonVariantProps, "children" | "onClick">
  & ArrowType & {
    klass?: KlassProp;
    fallbackPage: AnyPageMeta;
    pageHistoryOldToNew: PageHistoryOldToNew;
    dispatch: Dispatch<RouterAction>;
  };

export const ButtonLinkPageLast = (props: PropsWithChildren<ButtonLinkPageLastProps>) => {
  const buttonLinkProps = delProp(props, "arrowType", "fallbackPage", "pageHistoryOldToNew", "dispatch");

  const backPage = pipe(
    props.pageHistoryOldToNew[props.pageHistoryOldToNew.length - secondToLastIndex],
    O.fromNullable,
    O.getOrElse(() => props.fallbackPage),
  );

  const pageLastTitle = pipe(
    backPage.dataMeta,
    O.chain(_ => _.customDisplayName),
    O.getOrElse(() => backPage.title())
  );
  return (
    <ButtonLink
      {...buttonLinkProps}
      klass={klassProp(arrowTypeKlassO(O.fromNullable(props.arrowType), props.klass), "mt-0")}
      onClick={() => getNavigateBackFn(props.dispatch)(backPage)}
    >
      Back to {pageLastTitle}
    </ButtonLink>
  );
};
